<template>
	<form @submit.prevent="save" ref="form">
		<b-loading :active="isLoading"></b-loading>
		<div class="modal-card">
			<header class="modal-card-head">
				<form ref="routeMapPdf" method="post" target="_blank" :action="urlToRouteMapPdf">
					<input type="hidden" name="map" :value="this.mapImage" />
					<button
						class="button is-small is-outlined is-pdf-link"
						:class="{ 'is-loading': isSubmitting }"
						type="button"
						@click="submitMapPdfForm"
						:disabled="isSubmitting || isMapCentering"
						v-if="isAdmin || canNonAdminSeeTopButtons"
					>
						<i class="mdi">picture_as_pdf</i> PDF
					</button>
				</form>
				<form
					ref="routeMapDoc"
					method="post"
					target="_blank"
					:action="urlToRouteMapDoc"
					v-if="!isLite && (isAdmin || canNonAdminSeeTopButtons)"
				>
					<input type="hidden" name="map" :value="this.mapImage" />
					<button
						class="button is-small is-outlined is-doc-link"
						:class="{ 'is-loading': isSubmitting }"
						type="button"
						@click="submitMapDocForm"
						:disabled="isSubmitting || isMapCentering"
					>
						<i class="mdi">description</i> DOC
					</button>
				</form>
				<a
					:href="urlToRouteProductionPdf"
					target="_blank"
					class="button is-small is-outlined is-pdf-link"
					v-if="isRouteProductionPdfAvailable"
				>
					<i class="mdi">picture_as_pdf</i> Produkcja PDF
				</a>
				<a
					:href="urlToRouteProductionDoc"
					target="_blank"
					class="button is-small is-outlined is-doc-link"
					v-if="isRouteProductionDocAvailable"
				>
					<i class="mdi">description</i> Produkcja DOC
				</a>
				<p class="modal-card-title">
					Szczegóły trasy:
					<b>{{ currentRoute.name }}</b>
				</p>
				<button
					type="button"
					class="delete"
					aria-label="close"
					@click="$parent.$emit('close')"
				></button>
			</header>
			<section class="modal-card-body">
				<div class="columns">
					<div class="column">
						<h2>{{ isEditModeEnabled ? 'Edycja' : 'Szczegóły' }} trasy</h2>
						<div class="group">
							<div class="field is-horizontal">
								<div class="field-label">Nazwa trasy:</div>
								<editable
									:isEditMode="isEditModeEnabled"
									:value="currentRoute.name"
									required
									placeholder="Nazwa trasy"
									type="text"
									@input="currentRoute.name = $event"
								/>
							</div>
							<div class="field is-horizontal">
								<div class="field-label">Dodał:</div>
								<div class="field field-body is-grouped">
									<span>{{ currentRoute.creator ? currentRoute.creator.username : '-' }}</span>
								</div>
							</div>
							<div class="field is-horizontal">
								<div class="field-label">Kolor:</div>
								<div class="field-body">
									<span class="route-color" v-if="currentRoute.color">
										<span
											:style="`background-color: ${getColorByName(currentRoute.color).hex};`"
											class="route-color__preview"
										></span>
										<span class="route-color__name">{{
											getColorByName(currentRoute.color).name
										}}</span>
									</span>
									<i class="no-route-color" v-else>-- brak --</i>
									<button
										type="button"
										class="button is-small change-color"
										@click.prevent="isColorPickerActive = true"
										v-if="isEditModeEnabled"
									>
										Zmień
									</button>
								</div>
							</div>
							<hr />
							<div class="field is-horizontal">
								<div class="field-label">Status:</div>
								<div class="field-body">
									<b-dropdown :disabled="!canModifyRoute">
										<button
											class="button is-info is-small"
											slot="trigger"
											@click.prevent
											type="button"
										>
											<span>{{ currentRoute.status | formatStatus }}</span>
											<i class="mdi">arrow_drop_down</i>
										</button>
										<b-dropdown-item
											@click="changeStatus('planned')"
											v-if="
												currentRoute.status === 'trashed' ||
												currentRoute.status === 'complaint' ||
												currentRoute.status === 'sent' ||
												currentRoute.status === 'archived' ||
												currentRoute.status === 'inProduction'
											"
										>
											Zmień na
											<b>"Zaplanowane"</b>
										</b-dropdown-item>
										<b-dropdown-item
											@click="changeStatus('inProduction')"
											v-if="
												isInProductionStatusEnabled &&
												(currentRoute.status === 'trashed' ||
													currentRoute.status === 'complaint' ||
													currentRoute.status === 'planned' ||
													currentRoute.status === 'archived')
											"
										>
											Zmień na
											<b>"Produkcja"</b>
										</b-dropdown-item>
										<b-dropdown-item
											@click="changeStatus('sent')"
											v-if="
												currentRoute.status === 'trashed' ||
												currentRoute.status === 'complaint' ||
												currentRoute.status === 'planned' ||
												currentRoute.status === 'archived' ||
												currentRoute.status === 'inProduction'
											"
										>
											Zmień na
											<b>"Wysłane"</b>
										</b-dropdown-item>
										<b-dropdown-item
											@click="changeStatus('trashed')"
											v-if="
												currentRoute.status === 'complaint' ||
												currentRoute.status === 'sent' ||
												currentRoute.status === 'planned' ||
												currentRoute.status === 'archived' ||
												currentRoute.status === 'inProduction'
											"
										>
											Przenieś do <b>"Kosz"</b>
										</b-dropdown-item>
									</b-dropdown>
								</div>
							</div>
							<div class="field is-horizontal" v-if="isAdmin || agentsCanSeeRouteTotals">
								<div class="field-label">Suma zamówień:</div>
								<div class="field-body total-prices">
									<p v-if="routeTotalPrice.PLN">{{ routeTotalPrice.PLN }} zł</p>
									<p v-if="routeTotalPrice.CZK">{{ routeTotalPrice.CZK }} Kč</p>
									<p v-if="routeTotalPrice.EUR">{{ routeTotalPrice.EUR }} EUR</p>
									<p v-if="routeTotalPrice.HUF">{{ routeTotalPrice.HUF }} HUF</p>
									<p v-if="routeTotalPrice.RON">{{ routeTotalPrice.RON }} RON</p>
									<p
										v-if="
											!routeTotalPrice.PLN &&
											!routeTotalPrice.CZK &&
											!routeTotalPrice.EUR &&
											!routeTotalPrice.HUF &&
											!routeTotalPrice.RON
										"
									>
										-
									</p>
								</div>
							</div>
							<div class="field is-horizontal" v-if="isAdmin || agentsCanSeeRouteTotals">
								<div class="field-label">Suma przelewem:</div>
								<div class="field-body total-prices">
									<p v-if="routeTotalPriceByTransfer.PLN">{{ routeTotalPriceByTransfer.PLN }} zł</p>
									<p v-if="routeTotalPriceByTransfer.CZK">{{ routeTotalPriceByTransfer.CZK }} Kč</p>
									<p v-if="routeTotalPriceByTransfer.EUR">
										{{ routeTotalPriceByTransfer.EUR }} EUR
									</p>
									<p v-if="routeTotalPriceByTransfer.HUF">
										{{ routeTotalPriceByTransfer.HUF }} HUF
									</p>
									<p v-if="routeTotalPriceByTransfer.RON">
										{{ routeTotalPriceByTransfer.RON }} RON
									</p>
									<p
										v-if="
											!routeTotalPriceByTransfer.PLN &&
											!routeTotalPriceByTransfer.CZK &&
											!routeTotalPriceByTransfer.EUR &&
											!routeTotalPriceByTransfer.HUF &&
											!routeTotalPriceByTransfer.RON
										"
									>
										-
									</p>
								</div>
							</div>
							<div class="field is-horizontal" v-if="isAdmin || agentsCanSeeRouteTotals">
								<div class="field-label">Suma gotówką:</div>
								<div class="field-body total-prices">
									<p v-if="routeTotalPriceByCash.PLN">{{ routeTotalPriceByCash.PLN }} zł</p>
									<p v-if="routeTotalPriceByCash.CZK">{{ routeTotalPriceByCash.CZK }} Kč</p>
									<p v-if="routeTotalPriceByCash.EUR">{{ routeTotalPriceByCash.EUR }} EUR</p>
									<p v-if="routeTotalPriceByCash.HUF">{{ routeTotalPriceByCash.HUF }} HUF</p>
									<p v-if="routeTotalPriceByCash.RON">{{ routeTotalPriceByCash.RON }} RON</p>
									<p
										v-if="
											!routeTotalPriceByCash.PLN &&
											!routeTotalPriceByCash.CZK &&
											!routeTotalPriceByCash.EUR &&
											!routeTotalPriceByCash.HUF &&
											!routeTotalPriceByCash.RON
										"
									>
										-
									</p>
								</div>
							</div>
							<!-- <div class="field is-horizontal">
                <div class="field-label">Suma zamówień PLN:</div>
                <div class="field-body">
                  <p>{{ routeTotalPrice.PLN }} zł</p>
                </div>
              </div>
              <div class="field is-horizontal">
                <div class="field-label">Suma zamówień CZK:</div>
                <div class="field-body">
                  <p>{{ routeTotalPrice.CZK }} Kč</p>
                </div>
							</div>-->
							<div class="field is-horizontal" v-if="agentsCanEditEverything">
								<div class="field-label">Długość trasy:</div>
								<div class="field-body">{{ routeTotalDistance }}</div>
							</div>
						</div>

						<h2 v-if="isRouteDocumentAttachmentEnabled">Załącznik</h2>
						<div v-if="isRouteDocumentAttachmentEnabled" class="route-route-attachment">
							<div v-if="!currentRoute.id" class="route-route-attachment__empty">
								<p class="picture__empty">Załącznik będzie można dodać po zapisaniu trasy.</p>
							</div>
							<div v-else class="route-route-attachment__body">
								<div v-if="isRouteAttachmentDeleting" class="route-attachment__deleting">
									<b-loading :active="true" :is-full-page="false"></b-loading>
								</div>
								<template v-else>
									<div class="attachments-list">
										<div v-if="routeAttachmentMedia" class="attachment-item">
											<div class="attachment-item__layout">
													<div class="attachment-item__preview">
														<div
															v-if="isRouteAttachmentUploading"
															class="attachment-item__preview-loading"
														>
															<b-loading :active="true" :is-full-page="false"></b-loading>
														</div>
														<a
															v-else
															:href="routeAttachmentDocUrl"
															target="_blank"
															rel="noopener noreferrer"
															class="route-doc-badge-link"
															:title="routeAttachmentLinkTitle"
															:aria-label="'Pobierz: ' + routeAttachmentLinkCaption"
														>
															<div
																class="route-doc-badge route-doc-badge--compact"
																:class="'route-doc-badge--' + routeAttachmentDocBadge.kind"
															>
																<span class="route-doc-badge__ext-chip">{{
																	routeAttachmentDocBadge.extLabel
																}}</span>
															</div>
														</a>
													</div>
													<div class="attachment-item__main">
														<div class="attachment-item__header-row">
															<div class="attachment-item__title-block">
																<a
																	class="route-attachment-filename"
																	:href="routeAttachmentDocUrl"
																	target="_blank"
																	rel="noopener noreferrer"
																	:title="routeAttachmentLinkTitle"
																	:aria-label="'Pobierz: ' + routeAttachmentLinkCaption"
																>
																	<span class="route-attachment-filename__text">{{
																		routeAttachmentLinkCaption
																	}}</span>
																	<i class="mdi route-attachment-filename__ext-icon">open_in_new</i>
																</a>
																<p v-if="routeAttachmentMetaLine" class="attachment-item__meta">
																	{{ routeAttachmentMetaLine }}
																</p>
															</div>
															<div
																v-if="canEditRouteAttachment"
																class="attachment-item__header-actions"
															>
																<b-upload
																	v-model="routeAttachmentFile"
																	:accept="routeDocumentUploadAccept"
																	@input="onRouteAttachmentFileInput"
																	:disabled="isRouteAttachmentUploading"
																	class="route-attachment-change-upload"
																>
																	<span
																		class="button is-info is-small"
																		:class="{
																			'is-loading': isRouteAttachmentUploading,
																			'is-disabled': isRouteAttachmentUploading
																		}"
																		role="button"
																		tabindex="0"
																	>
																		<i class="mdi">edit</i>
																		<span>Zmień</span>
																	</span>
																</b-upload>
																<button
																	type="button"
																	class="button is-small is-light route-attachment-desc-toggle"
																	@click="onRouteAttachmentDescriptionToggle"
																>
																	<span>{{ routeAttachmentDescriptionPanelToggleLabel }}</span>
																</button>
																<button
																	type="button"
																	class="button is-danger is-light is-small"
																	:disabled="isRouteAttachmentUploading"
																	@click="deleteRouteAttachment"
																>
																	<i class="mdi">delete</i>
																	<span>Usuń</span>
																</button>
															</div>
														</div>
														<div
															v-if="canEditRouteAttachment && isRouteAttachmentDescriptionOpen"
															class="attachment-item__description-panel"
														>
															<b-field class="attachment-item__desc">
																<b-input
																	v-model="routeAttachmentDescriptionDraft"
																	type="textarea"
																	rows="4"
																	placeholder="Opcjonalny opis"
																	:disabled="
																		isRouteAttachmentUploading ||
																		isSavingRouteAttachmentDescription ||
																		isClearingRouteAttachmentDescription
																	"
																/>
															</b-field>
															<div class="attachment-item__save-row">
																<button
																	type="button"
																	class="button is-info is-small"
																	:class="{ 'is-loading': isSavingRouteAttachmentDescription }"
																	:disabled="
																		isRouteAttachmentUploading ||
																		isSavingRouteAttachmentDescription ||
																		isClearingRouteAttachmentDescription ||
																		!isRouteAttachmentDescriptionDirty
																	"
																	@click="persistRouteAttachmentDescription"
																>
																	<i class="mdi">save</i>
																	<span>Zapisz</span>
																</button>
																<button
																	type="button"
																	class="button is-danger is-light is-small"
																	:class="{ 'is-loading': isClearingRouteAttachmentDescription }"
																	:disabled="
																		isRouteAttachmentUploading ||
																		isSavingRouteAttachmentDescription ||
																		isClearingRouteAttachmentDescription ||
																		!canClearRouteAttachmentDescription
																	"
																	@click="clearRouteAttachmentDescription"
																>
																	<i class="mdi">delete</i>
																	<span>Usuń opis</span>
																</button>
															</div>
														</div>
														<p
															v-else-if="!canEditRouteAttachment && routeAttachmentDescriptionRead"
															class="attachment-item__desc-read"
														>
															{{ routeAttachmentDescriptionRead }}
														</p>
													</div>
												</div>
											</div>
											<div
												v-if="canEditRouteAttachment && !routeAttachmentMedia"
												class="attachment-upload-tile"
											>
												<b-upload
													v-model="routeAttachmentFile"
													:accept="routeDocumentUploadAccept"
													@input="onRouteAttachmentFileInput"
													:loading="isRouteAttachmentUploading"
													:disabled="isRouteAttachmentDeleting || isRouteAttachmentUploading"
													class="attachment-upload-tile__control"
												>
													<div
														class="attachment-upload-tile__inner"
														:class="{ 'is-loading': isRouteAttachmentUploading }"
													>
														<div
															v-if="isRouteAttachmentUploading"
															class="attachment-upload-tile__spinner"
														>
															<b-loading :active="true" :is-full-page="false"></b-loading>
														</div>
														<template v-else>
															<span class="attachment-upload-tile__text">+ Dodaj</span>
															<small>(0/1)</small>
															<small class="attachment-upload-tile__hint attachment-upload-tile__hint-line">{{
																routeDocumentUploadHintFormatsLine
															}}</small>
															<small class="attachment-upload-tile__hint attachment-upload-tile__hint-line">{{
																routeDocumentUploadHintMaxSizeLine
															}}</small>
														</template>
													</div>
												</b-upload>
											</div>
										</div>
								</template>
							</div>
						</div>

						<h2 v-if="agentsCanEditEverything">Wizualizacja trasy</h2>
						<div class="group" v-if="agentsCanEditEverything">
							<div class="map-controls">
								<b-switch v-model="areMarkersVisible">pokaż markery</b-switch>
								<b-switch v-model="areMarkersLabelsVisible" :disabled="!areMarkersVisible"
									>pokaż nr. zamówień</b-switch
								>
							</div>
							<div id="route-map" :class="{ pdf: isSubmitting, 'mapboxgl-map': true }"></div>
						</div>
					</div>
					<div class="column is-two-thirds" v-if="!isEditModeEnabled">
						<h2>Zamówienia</h2>
						<div class="group orders">
							<div class="change-start-point-wrapper" v-if="canChangeStartPoint">
								<div class="start-point-actions" v-if="!isChangeStartPointModeEnabled">
									<button
										type="button"
										class="button is-info"
										@click="isChangeStartPointModeEnabled = true"
									>
										{{ startPointActionLabel }}
									</button>
									<button
										type="button"
										class="button is-warning reverse-order-btn"
										@click="reverseOrder"
									>
										<i class="mdi">rotate_left</i> Odwróć kolejność
									</button>
								</div>
								<p class="help is-info start-point-help" v-if="!isChangeStartPointModeEnabled && !currentRoute.startPoint">
									Domyślnie startem jest pierwszy adres na liście.
								</p>
								<div v-if="isChangeStartPointModeEnabled">
									<b-field label="Adres punktu startowego" grouped></b-field>
									<b-field grouped>
										<b-autocomplete
											:data="filteredDataArray"
											placeholder="12-123 Kraków, Ślężna 12"
											icon="magnify"
											clearable
											expanded
											:loading="isStartPointFetching"
											field="display_label"
											@input="getStartPointLocation"
											@select="(option) => (startPoint = option)"
										>
											<template #empty>Brak pasujących wyników.</template>
										</b-autocomplete>
										<button
											type="button"
											class="button is-danger cancel-start-point"
											@click="isChangeStartPointModeEnabled = false"
										>
											Anuluj
										</button>
										<button
											type="button"
											class="button is-success save-start-point"
											@click="saveStartPoint"
										>
											Zapisz
										</button>
									</b-field>
								</div>
							</div>

							<hr v-if="canChangeStartPoint" />

							<div class="order-item order-item--start" v-if="currentRoute.startPoint">
								<span>Punkt startowy</span>
								<span
									><i class="mdi">{{ getMarkerIconName(0, 0, false) }}</i>
									{{ JSON.parse(currentRoute.startPoint).display_name }}</span
								>
								<div class="action">
									<button type="button" class="button is-small is-danger" @click="deleteStartPoint">
										Usuń punkt startowy
									</button>
								</div>
							</div>

							<draggable
								v-if="routeOrders.length"
								v-model="routeOrders"
								tag="ul"
								v-bind="dragOptions"
								class="orders-list"
								:move="onMoveDraggable"
								:disabled="!canModifyRoute"
							>
								<transition-group type="transition">
									<li
										v-for="(order, index) in routeOrders"
										:key="order.id"
										class="order-item"
										:disabled="!order.lat || !order.lon"
									>
										<!-- <svg
											class="flag"
											v-if="order.currency === 'PLN'"
											xmlns="http://www.w3.org/2000/svg"
											version="1.0"
											viewBox="0 0 30 20"
										>
											<rect x="0" y="0" width="100%" height="50%" fill="#fff" />
											<rect x="0" y="50%" width="100%" height="50%" fill="#d7141a" />
										</svg>
										<svg
											class="flag"
											v-if="order.currency === 'CZK'"
											xmlns="http://www.w3.org/2000/svg"
											version="1.0"
											viewBox="0 0 30 20"
										>
											<rect x="0" y="0" width="100%" height="50%" fill="#fff" />
											<rect x="0" y="50%" width="100%" height="50%" fill="#d7141a" />
											<path d="M 15,10 0,0 V 20 z" fill="#11457e" />
										</svg>-->
										<b class="order-no">{{ index + 1 }}</b>
										<div
											class="montage"
											v-if="canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)"
										>
											<p class="price">
												Cena {{ order.price }} {{ getCurrencyLabel(order.currency) }}
											</p>
											<p class="montage-date">
												<i class="mdi">build</i>
												<flat-pickr
													placeholder="godz."
													class="time-input"
													:config="timePickerConfig"
													:value="normalizeMontageTime(order.montageTime)"
													:disabled="isLoading"
													@on-open="setChangingTimeModeActive"
													@on-close="(selectedDates) => changeMontageTime(order, selectedDates)"
												/>
												<flat-pickr
													placeholder="data"
													class="date-input"
													:config="datePickerConfig"
													:value="normalizeMontageDate(order.montageDate)"
													:disabled="isLoading || isChangingTimeModeActive"
													@on-close="(selectedDates) => changeMontageDate(order, selectedDates)"
												/>
												<!-- <button @click.prevent="clearMontageDateTime(order)">clear</button> -->
											</p>
											<div v-if="!isLite">
												<b-switch
													:value="order.isApproved"
													:disabled="isLoading || isChangingTimeModeActive"
													@input="(isApproved) => changeIsApprovedStatus(order, isApproved)"
													type="is-warning"
													>zatwierdzone?</b-switch
												>
											</div>
											<div v-if="!isLite">
												<b-switch
													:value="order.isInvoiceSent"
													:disabled="isLoading || isChangingTimeModeActive"
													@input="
														(isInvoiceSent) => changeIsInvoiceSentStatus(order, isInvoiceSent)
													"
													type="is-info"
													>fv wysłana?</b-switch
												>
											</div>
											<div v-if="!isLite">
												<b-switch
													:value="order.isInvoicePaid"
													:disabled="isLoading || isChangingTimeModeActive"
													@input="
														(isInvoicePaid) => changeIsInvoicePaidStatus(order, isInvoicePaid)
													"
													type="is-danger"
													>fv zapłacona?</b-switch
												>
											</div>
										</div>
										<div class="client-order">
											<a
												href
												:disabled="
													!canNonAdminSeeRouteAsAdmin && user.id !== getOrderCreatorId(order)
												"
												@click.prevent="
													canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)
														? $parent.$emit('order-click', order)
														: null
												"
												class="link"
												>#{{ order.id }}
												{{
													canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)
														? order.clientName
														: '(ukryte)'
												}}</a
											>
											<p class="phone">
												<i class="mdi">phone</i>
												{{
													canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)
														? order.phone
														: '(ukryte)'
												}}
											</p>
											<p class="email">
												<i class="mdi">email</i>
												{{
													canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)
														? (order.email || '(brak)') + ' '
														: '(ukryte)'
												}}
											</p>
										</div>
										<div
											class="distance"
											v-if="
												(!order.lat || !order.lon) &&
												(canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order))
											"
										>
											<span class="no-localization">
												<i class="mdi">close</i> brak lokalizacji!
											</span>
										</div>
										<div
											class="distance"
											v-if="
												index === 0 &&
												(canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)) &&
												distances.length > 0 &&
												!currentRoute.startPoint
											"
										>
											<i class="mdi">{{ getMarkerIconName(index, distances.length) }}</i>
											Start trasy
										</div>
										<div
											class="distance distance-start"
											v-else-if="
												(canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)) &&
												distances.length > 0 &&
												index <= (currentRoute.startPoint ? distances.length - 1 : distances.length)
											"
										>
											<i class="mdi">{{
												getMarkerIconName(index, distances.length, !!currentRoute.startPoint)
											}}</i>
											{{
												distances[currentRoute.startPoint ? index : index - 1] + ' KM' ||
												order.distance + ' KM' ||
												'-'
											}}
											<p v-show="index === distances.length + (currentRoute.startPoint ? -1 : 0)">
												Koniec trasy
											</p>
										</div>
										<div
											class="action"
											v-if="
												!order.emailSent &&
												(canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)) &&
												!canSendAsUser
											"
										>
											<button
												type="button"
												class="button is-primary"
												:disabled="!order.email || isLoading || isChangingTimeModeActive"
												@click="sendEmail(order.id, 'system')"
											>
												Wyślij email
											</button>
										</div>
										<div
											class="action"
											v-if="
												order.emailSent &&
												(canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)) &&
												!canSendAsUser
											"
										>
											<span class="email-sent-label">Email wysłano</span>
											<span class="email-sent-date">{{
												formatDate(order.emailSent, 'HH:mm DD/MM/YYYY')
											}}</span>
											<button
												type="button"
												class="button is-small"
												:disabled="!order.email || isLoading || isChangingTimeModeActive"
												@click="sendEmail(order.id, 'system')"
											>
												Wyślij ponownie
											</button>
										</div>
										<div
											class="action"
											v-if="
												!order.emailSent &&
												(canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)) &&
												canSendAsUser
											"
										>
											<b-dropdown
												position="is-bottom-left"
												:disabled="!order.email || isLoading || isChangingTimeModeActive"
											>
												<button
													class="button is-primary"
													slot="trigger"
													@click.prevent
													type="button"
												>
													<span>Wyślij jako</span>
													<i class="mdi">arrow_drop_down</i>
												</button>
												<b-dropdown-item
													v-if="
														user.mailerIsEnabled &&
														user.mailerIsActive &&
														(!user.mailerMode || user.mailerMode === 'regular')
													"
													@click="sendEmail(order.id, 'user')"
													><b class="item-email">{{ user.mailerEmail }}</b> (Twój
													email)</b-dropdown-item
												>
												<b-dropdown-item
													v-if="
														user.mailerIsEnabled &&
														user.mailerIsActive &&
														user.mailerMode === 'gmail'
													"
													@click="sendEmail(order.id, 'user')"
													><b class="item-email">{{ user.googleEmail }}</b> (Twój
													email)</b-dropdown-item
												>
												<b-dropdown-item
													@click="sendEmail(order.id, 'system')"
													v-if="isSystemMailerOptionVisible"
													><b class="item-email">{{ systemEmail }}</b> (systemowy)</b-dropdown-item
												>
											</b-dropdown>
										</div>
										<div
											class="action"
											v-if="
												order.emailSent &&
												(canNonAdminSeeRouteAsAdmin || user.id === getOrderCreatorId(order)) &&
												canSendAsUser
											"
										>
											<span class="email-sent-label">Email wysłano</span>
											<span class="email-sent-date">{{
												formatDate(order.emailSent, 'HH:mm DD/MM/YYYY')
											}}</span>
											<b-dropdown
												position="is-bottom-left"
												:disabled="!order.email || isLoading || isChangingTimeModeActive"
											>
												<button class="button is-small" slot="trigger" @click.prevent>
													<span>Wyślij ponownie jako</span>
													<i class="mdi">arrow_drop_down</i>
												</button>
												<b-dropdown-item
													v-if="
														user.mailerIsEnabled &&
														user.mailerIsActive &&
														(!user.mailerMode || user.mailerMode === 'regular')
													"
													@click="sendEmail(order.id, 'user')"
													><b class="item-email">{{ user.mailerEmail }}</b> (Twój
													email)</b-dropdown-item
												>
												<b-dropdown-item
													v-if="
														user.mailerIsEnabled &&
														user.mailerIsActive &&
														user.mailerMode === 'gmail'
													"
													@click="sendEmail(order.id, 'user')"
													><b class="item-email">{{ user.googleEmail }}</b> (Twój
													email)</b-dropdown-item
												>
												<b-dropdown-item
													@click="sendEmail(order.id, 'system')"
													v-if="isSystemMailerOptionVisible"
													><b class="item-email">{{ systemEmail }}</b> (systemowy)</b-dropdown-item
												>
											</b-dropdown>
										</div>
									</li>
								</transition-group>
							</draggable>
							<p v-else class="no-orders-info">Brak przypisanych zamówień do tej trasy.</p>
						</div>
					</div>
				</div>
			</section>
			<footer class="modal-card-foot">
				<div class="level">
					<div class="level-left">
						<button type="submit" class="button is-success button--save" v-if="isEditModeEnabled">
							<i class="mdi">save</i> Zapisz zmiany
						</button>
						<button
							class="button is-primary button--edit"
							:disabled="!canModifyRoute"
							@click.prevent="isEditModeEnabled = true"
							v-else
						>
							<i class="mdi">edit</i> Edytuj
						</button>
						<button class="button is-white button--cancel" @click.prevent="$parent.$emit('close')">
							Zamknij
						</button>
					</div>
					<div class="level-right">
						<button
							class="button is-danger is-outlined button--archive"
							:disabled="!canModifyRoute"
							@click.prevent="archive"
							v-if="currentRoute.status !== 'archived'"
						>
							Archiwizuj
						</button>
						<button
							:disabled="!canModifyRoute"
							class="button is-danger is-small button--delete"
							@click.prevent="showDeleteDialog"
						>
							Usuń
						</button>
					</div>
				</div>
			</footer>
		</div>

		<b-modal :active="isColorPickerActive" has-modal-card @close="isColorPickerActive = false">
			<color-picker
				:preselected="getColorByName(currentRoute.color)"
				@select="currentRoute.color = $event.name"
			/>
		</b-modal>
	</form>
</template>

<script>
import moment from 'moment'
import debounce from 'lodash/debounce'
import { mapState, mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import flatPickr from 'vue-flatpickr-component'
import { Polish } from 'flatpickr/dist/l10n/pl'
import 'flatpickr/dist/flatpickr.css'
import mbxDirections from '@mapbox/mapbox-sdk/services/directions'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import html2canvas from 'html2canvas'

import {
	getColorByHex,
	getColorByName,
	normalizeApiMontageDate,
	normalizeApiMontageTime
} from '@/helpers'
import bus from '@/EventBus'
import config from '@/config'
import EditableField from '@/components/EditableField'
import ColorPicker from '@/components/ColorPicker'
import LocationService from '@/services/LocationService'
import UploadService from '@/services/UploadService'
import {
	extractUploadErrorMessage,
	ROUTE_DOCUMENT_UPLOAD_ACCEPT_ATTRIBUTE,
	ROUTE_DOCUMENT_UPLOAD_HINT_FORMATS_LINE,
	ROUTE_DOCUMENT_UPLOAD_HINT_MAX_SIZE_LINE,
	validateRouteDocumentFile
} from '@/utils/uploadValidation'
import {
	isSplitPaymentCashRemainder,
	countsTowardTransferTotal
} from '@/utils/paymentMethod'

export default {
	name: 'ViewRouteModal',
	components: {
		editable: EditableField,
		ColorPicker,
		draggable,
		flatPickr
	},
	data() {
		return {
			isLoading: false,
			isOrderUpdating: false,
			isChangingTimeModeActive: false,
			isEditModeEnabled: false,
			isColorPickerActive: false,
			selectedColor: null,
			datePickerConfig: {
				dateFormat: 'd-m-Y',
				locale: Polish
			},
			timePickerConfig: {
				enableTime: true,
				noCalendar: true,
				dateFormat: 'H:i',
				time_24hr: true,
				defaultHour: 7,
				defaultMinute: 0
			},
			distances: [],
			map: null,
			routeGeometryUuid: null,
			areMarkersVisible: true,
			areMarkersLabelsVisible: true,
			prevRouteOrders: [],
			markers: [],
			isChangeStartPointModeEnabled: false,
			filteredDataArray: [],
			startPoint: null,
			isStartPointFetching: false,
			mapImage: '',
			isSubmitting: false,
			isMapCentering: false,
			routeAttachmentFile: [],
			isRouteAttachmentUploading: false,
			isRouteAttachmentDeleting: false,
			isSavingRouteAttachmentDescription: false,
			isClearingRouteAttachmentDescription: false,
			routeAttachmentDescriptionDraft: '',
			isRouteAttachmentDescriptionOpen: false
		}
	},
	computed: {
		...mapState('routes', ['currentRoute']),
		...mapGetters('auth', ['isAdmin', 'user']),
		routeTotalPrice() {
			const prices = {
				PLN: 0,
				CZK: 0,
				EUR: 0,
				HUF: 0,
				RON: 0
			}
			if (this.routeOrders) {
				prices.PLN = this.routeOrders.reduce((prev, current) => {
					return current.currency === 'PLN' ? prev + Number(current.price) : prev
				}, 0)
				prices.CZK = this.routeOrders.reduce((prev, current) => {
					return current.currency === 'CZK' ? prev + Number(current.price) : prev
				}, 0)
				prices.EUR = this.routeOrders.reduce((prev, current) => {
					return current.currency === 'EUR' ? prev + Number(current.price) : prev
				}, 0)
				prices.HUF = this.routeOrders.reduce((prev, current) => {
					return current.currency === 'HUF' ? prev + Number(current.price) : prev
				}, 0)
				prices.RON = this.routeOrders.reduce((prev, current) => {
					return current.currency === 'RON' ? prev + Number(current.price) : prev
				}, 0)
			}

			return prices
		},
		routeTotalPriceByTransfer() {
			return {
				PLN: this.sumTransferByCurrency('PLN'),
				CZK: this.sumTransferByCurrency('CZK'),
				EUR: this.sumTransferByCurrency('EUR'),
				HUF: this.sumTransferByCurrency('HUF'),
				RON: this.sumTransferByCurrency('RON')
			}
		},
		routeTotalPriceByCash() {
			return {
				PLN: this.sumCashByCurrency('PLN'),
				CZK: this.sumCashByCurrency('CZK'),
				EUR: this.sumCashByCurrency('EUR'),
				HUF: this.sumCashByCurrency('HUF'),
				RON: this.sumCashByCurrency('RON')
			}
		},
		routeTotalDistance() {
			if (!this.distances.length) return '-'
			const total = this.distances.reduce((prev, current) => {
				return prev + Number(current)
			}, 0)
			return total + ' km'
		},
		urlToRouteDoc() {
			const token = localStorage.getItem('token')
			return config.service.routeDocURL + this.currentRoute.id + '?bearer=' + token
		},
		urlToRouteProductionPdf() {
			const token = localStorage.getItem('token')
			return config.service.routeProductionPdfURL + this.currentRoute.id + '?bearer=' + token
		},
		urlToRouteProductionDoc() {
			const token = localStorage.getItem('token')
			return config.service.routeProductionDocURL + this.currentRoute.id + '?bearer=' + token
		},
		urlToRouteMapPdf() {
			const token = localStorage.getItem('token')
			const routePFDURL = config.company.shouldPDFRouteViewIncludeMap
				? config.service.routeMapPdfURL
				: config.service.routePdfURL
			return routePFDURL + this.currentRoute.id + '?bearer=' + token
		},
		urlToRouteMapDoc() {
			const token = localStorage.getItem('token')
			const routeDOCURL = config.company.shouldDOCRouteViewIncludeMap
				? config.service.routeMapDocURL
				: config.service.routeDocURL
			return routeDOCURL + this.currentRoute.id + '?bearer=' + token
		},
		dragOptions() {
			return {
				animation: 200,
				group: 'description',
				disabled: false,
				ghostClass: 'ghost'
			}
		},
		routeOrders: {
			get() {
				if (this.isLoading && !this.isOrderUpdating && this.prevRouteOrders.length)
					return this.prevRouteOrders

				const sortedOrdersWithLocations = []
				const nonSortedOrdersWithLocations = []
				const otherOrders = []

				if (!this.currentRoute || !this.currentRoute.orders || !this.currentRoute.orders.length)
					return []

				if (this.currentRoute.ordersIds) {
					const sortedOrdersIds = JSON.parse(this.currentRoute.ordersIds)
					sortedOrdersIds &&
						sortedOrdersIds.forEach((sortedOrderId) => {
							const foundOrder = this.currentRoute.orders.find(
								(order) => order.id === sortedOrderId && order.lat && order.lon
							)
							if (foundOrder) sortedOrdersWithLocations.push(foundOrder)
						})
				}

				this.currentRoute.orders.forEach((order) => {
					const foundOrder = sortedOrdersWithLocations.find(
						(orderWithLocation) => order.id === orderWithLocation.id
					)
					if (!foundOrder) {
						if (order.lat && order.lon) nonSortedOrdersWithLocations.push(order)
						else otherOrders.push(order)
					}
				})

				return [...sortedOrdersWithLocations, ...nonSortedOrdersWithLocations, ...otherOrders]
			},
			set(value) {
				const sortedOrdersIds = JSON.stringify(value.map(({ id }) => id))

				this.isLoading = true

				this.addOrderMarkers(value)

				this.getRouteDistances(value)
					.then(({ body }) => {
						if (!body.routes.length) {
							const error =
								'Nie można wyznaczyć trasy, proszę spróbować zmienić kolejność zamówień lub punkty na trasie.'
							this.$snackbar.open(error)
							console.log('getRouteDistances error', error)

							this.$store
								.dispatch('routes/updateRouteOrdersOrderAndDistances', {
									sortedOrdersIds,
									distances: []
								})
								.then((route) => {
									this.isLoading = false
									this.$snackbar.open(
										`Kolejność zamówień dla trasy #${route.id} została zmieniona.`
									)
									bus.$emit('route-updated')
								})
							return
						}

						this.updateDistances(body.routes[0].legs)
						this.addRouteLayer(body.routes[0].geometry.coordinates, body.uuid)

						this.$store
							.dispatch('routes/updateRouteOrdersOrderAndDistances', {
								sortedOrdersIds,
								distances: this.distances
							})
							.then((route) => {
								this.isLoading = false
								this.$snackbar.open(`Kolejność zamówień dla trasy #${route.id} została zmieniona.`)
								bus.$emit('route-updated')
							})
					})
					.catch((errorMsg) => {
						console.log('error', errorMsg)
						this.$snackbar.open(errorMsg)
						this.isLoading = false
					})

				this.centerMap()
				this.prevRouteOrders = [...value]
			}
		},
		routeCoordinates() {
			const coords = this.routeOrders
				.filter(({ lat, lon }) => lon && lat)
				.map(({ lat, lon }) => [lon, lat])

			if (this.currentRoute.startPoint) {
				const lat = Number(JSON.parse(this.currentRoute.startPoint).lat)
				const lon = Number(JSON.parse(this.currentRoute.startPoint).lon)
				return [...coords, [lon, lat]]
			}

			return coords
		},
		canNonAdminSeeRouteAsAdmin() {
			return this.isAdmin || config.company.canNonAdminSeeRouteAsAdmin
		},
		canNonAdminSeeTopButtons() {
			return this.canNonAdminSeeRouteAsAdmin || config.company.canNonAdminSeeTopButtons
		},
		agentsCanEditEverything() {
			return this.isAdmin || config.company.agentsCanEditEverything
		},
		agentsCanSeeRouteTotals() {
			return this.isAdmin || config.company.agentsCanSeeRouteTotals
		},
		canChangeStartPoint() {
			return config.company.canChangeStartPoint && (this.isAdmin || this.canNonAdminSeeRouteAsAdmin)
		},
		isRouteProductionPdfAvailable() {
			return config.company.isRouteProductionPdfAvailable
		},
		isRouteProductionDocAvailable() {
			return config.company.isRouteProductionDocAvailable
		},
		canModifyRoute() {
			return (
				this.isAdmin ||
				(this.agentsCanEditEverything && this.user.id === this.getRouteCreatorId(this.currentRoute))
			)
		},
		isLite() {
			return config.company.isLite
		},
		systemEmail() {
			return config.company.systemEmail
		},
		canSendAsUser() {
			return config.company.canSendAsUser
		},
		isInProductionStatusEnabled() {
			return config.company.isInProductionStatusEnabled
		},
		isSystemMailerOptionVisible() {
			if (config.company.isSystemMailerOptionVisible) return true
			const isUserMailerReady = this.user.mailerIsEnabled && this.user.mailerIsActive
			return isUserMailerReady ? false : true
		},
		currentStartPointDisplayName() {
			if (!this.currentRoute || !this.currentRoute.startPoint) return ''
			try {
				return JSON.parse(this.currentRoute.startPoint).display_name || ''
			} catch (error) {
				return ''
			}
		},
		startPointActionLabel() {
			return this.currentRoute && this.currentRoute.startPoint
				? 'Zmień własny punkt startowy'
				: 'Ustaw własny punkt startowy'
		},
		isRouteDocumentAttachmentEnabled() {
			return !!config.company.isRouteDocumentAttachmentEnabled
		},
		canEditRouteAttachment() {
			return this.isRouteDocumentAttachmentEnabled && this.canModifyRoute
		},
		routeAttachmentMedia() {
			const ra = this.currentRoute && this.currentRoute.routeAttachment
			return ra && ra.media ? ra.media : null
		},
		routeAttachmentDocUrl() {
			const m = this.routeAttachmentMedia
			if (!m) return ''
			if (m.filePath) {
				return config.service.apiRootURL + 'media/' + m.filePath
			}
			if (m.contentUrl) {
				const cu = String(m.contentUrl)
				if (/^https?:\/\//i.test(cu)) {
					return cu
				}
				return config.service.apiRootURL + cu.replace(/^\//, '')
			}
			return ''
		},
		/** Pełna nazwa pliku na serwerze (np. z uniqid) — tylko tooltip / diagnostyka. */
		routeAttachmentStoredFileBasename() {
			const m = this.routeAttachmentMedia
			if (!m) return ''
			const basename = (p) => {
				if (p == null || p === '') return ''
				const parts = String(p).split(/[/\\]/)
				const last = parts[parts.length - 1] || ''
				try {
					return decodeURIComponent(last)
				} catch (e) {
					return last
				}
			}
			if (m.filePath) {
				const n = basename(m.filePath)
				if (n) return n
			}
			if (m.contentUrl) {
				const n = basename(m.contentUrl)
				if (n) return n
			}
			return ''
		},
		/** Jak routeAttachmentStoredFileBasename, ale prefiks uniqid() z Vicha (13 znaków hex + _) → ... */
		routeAttachmentStoredFileBasenameForDisplay() {
			const raw = this.routeAttachmentStoredFileBasename
			if (!raw) {
				return ''
			}
			return raw.replace(/^[0-9a-f]{13}_/i, '...')
		},
		routeAttachmentLinkCaption() {
			const d = this.routeAttachmentStoredFileBasenameForDisplay
			return d || 'Załącznik'
		},
		routeAttachmentLinkTitle() {
			return 'Kliknij, aby wyświetlić lub pobrać plik'
		},
		routeAttachmentSavedHasDescription() {
			const ra = this.currentRoute && this.currentRoute.routeAttachment
			if (!ra || ra.description == null) return false
			return String(ra.description).trim() !== ''
		},
		routeAttachmentDescriptionPanelToggleLabel() {
			if (this.isRouteAttachmentDescriptionOpen) {
				return 'Zwiń opis'
			}
			return this.routeAttachmentSavedHasDescription ? 'Edytuj opis' : 'Dodaj opis'
		},
		routeAttachmentMetaLine() {
			const m = this.routeAttachmentMedia
			if (!m) return ''
			const parts = []
			const sizeVal = m.imageSize != null ? m.imageSize : m.image_size
			const sz = this.formatRouteAttachmentFileSize(sizeVal)
			if (sz) {
				parts.push(sz)
			}
			const updatedVal = m.updatedAt != null ? m.updatedAt : m.updated_at
			const dt = this.formatRouteAttachmentMediaDate(updatedVal)
			if (dt) {
				parts.push('Dodany: ' + dt)
			}
			return parts.length ? parts.join('  |  ') : ''
		},
		routeAttachmentDocBadge() {
			const m = this.routeAttachmentMedia
			const ref = m && (m.filePath || m.contentUrl)
			if (!m || !ref) {
				return { kind: 'unknown', extLabel: 'PLIK' }
			}
			const fp = String(ref).toLowerCase()
			if (fp.endsWith('.pdf')) {
				return { kind: 'pdf', extLabel: 'PDF' }
			}
			if (fp.endsWith('.docx')) {
				return { kind: 'docx', extLabel: 'DOCX' }
			}
			if (fp.endsWith('.doc')) {
				return { kind: 'doc', extLabel: 'DOC' }
			}
			const fromPath = (p) => {
				const parts = String(p).split(/[/\\]/)
				const last = parts[parts.length - 1] || ''
				const dot = last.lastIndexOf('.')
				if (dot < 0 || dot >= last.length - 1) {
					return ''
				}
				return last
					.slice(dot + 1)
					.toUpperCase()
					.replace(/[^A-Z0-9]/g, '')
					.slice(0, 6)
			}
			const guess = fromPath(fp) || fromPath(String(m.filePath || '')) || fromPath(String(m.contentUrl || ''))
			const extLabel = guess.length ? guess : 'PLIK'
			return { kind: 'unknown', extLabel }
		},
		routeDocumentUploadAccept() {
			return ROUTE_DOCUMENT_UPLOAD_ACCEPT_ATTRIBUTE
		},
		routeDocumentUploadHintFormatsLine() {
			return ROUTE_DOCUMENT_UPLOAD_HINT_FORMATS_LINE
		},
		routeDocumentUploadHintMaxSizeLine() {
			return ROUTE_DOCUMENT_UPLOAD_HINT_MAX_SIZE_LINE
		},
		routeAttachmentDescriptionRead() {
			const ra = this.currentRoute && this.currentRoute.routeAttachment
			if (!ra || ra.description == null || ra.description === '') return ''
			return ra.description
		},
		isRouteAttachmentDescriptionDirty() {
			const ra = this.currentRoute && this.currentRoute.routeAttachment
			const saved = ra && ra.description != null ? ra.description : ''
			return this.routeAttachmentDescriptionDraft !== saved
		},
		canClearRouteAttachmentDescription() {
			const ra = this.currentRoute && this.currentRoute.routeAttachment
			if (!ra) {
				return false
			}
			const savedNonEmpty = ra.description != null && String(ra.description).trim() !== ''
			const draftNonEmpty = String(this.routeAttachmentDescriptionDraft || '').trim() !== ''
			return savedNonEmpty || draftNonEmpty
		}
	},
	watch: {
		areMarkersVisible: function (areMarkersVisible) {
			document.querySelectorAll('.custom-marker').forEach((marker) => {
				if (areMarkersVisible) marker.classList.remove('is-hidden')
				else marker.classList.add('is-hidden')
				this.areMarkersLabelsVisible = areMarkersVisible
			})
		},
		areMarkersLabelsVisible: function (areMarkersVisible) {
			document.querySelectorAll('.custom-marker').forEach((marker) => {
				if (areMarkersVisible) marker.classList.remove('is-label-hidden')
				else marker.classList.add('is-label-hidden')
			})
		},
		'currentRoute.routeAttachment': {
			handler() {
				this.syncRouteAttachmentDescriptionDraft()
				this.syncRouteAttachmentDescriptionPanelOpen()
			},
			deep: true
		}
	},
	filters: {
		formatStatus(status) {
			return config.company.orderStatusNames[status]
		}
	},
	methods: {
		sumTransferByCurrency(currency) {
			if (!this.routeOrders) {
				return 0
			}
			return this.routeOrders.reduce((prev, current) => {
				if (current.currency !== currency) {
					return prev
				}
				if (isSplitPaymentCashRemainder(current.paymentMethod)) {
					return prev + Number(current.advance)
				}
				if (countsTowardTransferTotal(current.paymentMethod)) {
					return prev + Number(current.price)
				}
				return prev
			}, 0)
		},
		sumCashByCurrency(currency) {
			if (!this.routeOrders) {
				return 0
			}
			return this.routeOrders.reduce((prev, current) => {
				if (current.currency !== currency) {
					return prev
				}
				if (current.paymentMethod === 'gotówka') {
					return prev + Number(current.price)
				}
				if (isSplitPaymentCashRemainder(current.paymentMethod)) {
					return prev + Number(current.leftToPay)
				}
				return prev
			}, 0)
		},
		getColorByHex(hex) {
			return getColorByHex(hex)
		},
		getColorByName(name) {
			return getColorByName(name)
		},
		formatDate(value, format) {
			return moment(value).format(format)
		},
		syncRouteAttachmentDescriptionDraft() {
			const ra = this.currentRoute && this.currentRoute.routeAttachment
			this.routeAttachmentDescriptionDraft = ra && ra.description != null ? ra.description : ''
		},
		syncRouteAttachmentDescriptionPanelOpen() {
			const ra = this.currentRoute && this.currentRoute.routeAttachment
			if (!ra || !ra.media) {
				this.isRouteAttachmentDescriptionOpen = false
				return
			}
			const d = ra.description != null ? String(ra.description).trim() : ''
			if (d !== '') {
				this.isRouteAttachmentDescriptionOpen = true
			}
		},
		onRouteAttachmentDescriptionToggle() {
			if (this.isRouteAttachmentDescriptionOpen) {
				this.syncRouteAttachmentDescriptionDraft()
				this.isRouteAttachmentDescriptionOpen = false
			} else {
				this.isRouteAttachmentDescriptionOpen = true
			}
		},
		formatRouteAttachmentFileSize(bytes) {
			if (bytes == null || bytes === '' || Number.isNaN(Number(bytes))) {
				return ''
			}
			const n = Number(bytes)
			if (n < 1024) {
				return `${n} B`
			}
			if (n < 1024 * 1024) {
				return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`
			}
			return `${(n / (1024 * 1024)).toFixed(1)} MB`
		},
		formatRouteAttachmentMediaDate(value) {
			if (!value) {
				return ''
			}
			try {
				return moment(value).format('HH:mm DD.MM.YYYY')
			} catch (e) {
				return ''
			}
		},
		onRouteAttachmentFileInput() {
			const fileToUpload = this.routeAttachmentFile && this.routeAttachmentFile[0]
			if (!fileToUpload) return

			const validation = validateRouteDocumentFile(fileToUpload)
			if (!validation.valid) {
				this.$toast.open({
					type: 'is-danger',
					duration: 6000,
					message: validation.message
				})
				this.routeAttachmentFile = []
				return
			}

			this.isRouteAttachmentUploading = true
			this.$snackbar.open('Rozpoczęto wysyłkę załącznika...')

			const oldMedia = this.routeAttachmentMedia
			const oldAttachment = this.currentRoute.routeAttachment

			const formData = new FormData()
			formData.append('file', fileToUpload)

			UploadService.uploadDocument(formData)
				.then((response) => {
					const media = response.data
					const nextDesc =
						oldAttachment && oldAttachment.description != null ? oldAttachment.description : ''
					const next = { media, description: nextDesc }
					if (oldAttachment && oldAttachment['@id']) {
						this.$set(next, '@id', oldAttachment['@id'])
					}
					if (oldAttachment && oldAttachment.id != null) {
						this.$set(next, 'id', oldAttachment.id)
					}
					this.$set(this.currentRoute, 'routeAttachment', next)
					this.syncRouteAttachmentDescriptionDraft()
					this.$toast.open({
						type: 'is-success',
						message: 'Załącznik został wgrany na serwer, zapisywanie trasy...'
					})
					return this.$store.dispatch('routes/updateRoute').then(() => {
						if (oldMedia && oldMedia.id !== media.id) {
							return UploadService.deleteImage(oldMedia).catch((err) => {
								console.warn('Failed to delete old route attachment media:', err)
							})
						}
					})
				})
				.then(() => {
					this.$snackbar.open(`Trasa #${this.currentRoute.id} została zaktualizowana.`)
					bus.$emit('route-updated')
				})
				.catch((errors) => {
					this.$toast.open({
						type: 'is-danger',
						duration: 6000,
						message: extractUploadErrorMessage(errors)
					})
					console.log('route attachment upload errors', errors)
				})
				.finally(() => {
					this.isRouteAttachmentUploading = false
					this.routeAttachmentFile = []
				})
		},
		deleteRouteAttachment() {
			if (!this.routeAttachmentMedia) {
				return
			}
			this.isRouteAttachmentDeleting = true
			this.isRouteAttachmentDescriptionOpen = false
			const oldMedia = this.routeAttachmentMedia
			this.$set(this.currentRoute, 'routeAttachment', null)
			this.syncRouteAttachmentDescriptionDraft()
			this.$store
				.dispatch('routes/updateRoute')
				.then(() => UploadService.deleteImage(oldMedia))
				.then(() => {
					this.$snackbar.open(`Załącznik trasy #${this.currentRoute.id} został usunięty.`)
					bus.$emit('route-updated')
				})
				.catch((errors) => {
					this.$toast.open({
						type: 'is-danger',
						duration: 6000,
						message: extractUploadErrorMessage(errors)
					})
					console.log('route attachment delete errors', errors)
				})
				.finally(() => {
					this.isRouteAttachmentDeleting = false
				})
		},
		persistRouteAttachmentDescription() {
			if (!this.currentRoute.routeAttachment) {
				return
			}
			this.isSavingRouteAttachmentDescription = true
			this.$set(this.currentRoute.routeAttachment, 'description', this.routeAttachmentDescriptionDraft)
			this.$store
				.dispatch('routes/updateRoute')
				.then(() => {
					this.syncRouteAttachmentDescriptionDraft()
					this.syncRouteAttachmentDescriptionPanelOpen()
					const saved = this.currentRoute.routeAttachment && this.currentRoute.routeAttachment.description
					if (saved == null || String(saved).trim() === '') {
						this.isRouteAttachmentDescriptionOpen = false
					}
					this.$snackbar.open('Opis załącznika został zapisany.')
					bus.$emit('route-updated')
				})
				.catch((errors) => {
					this.$toast.open({
						type: 'is-danger',
						duration: 6000,
						message: extractUploadErrorMessage(errors)
					})
				})
				.finally(() => {
					this.isSavingRouteAttachmentDescription = false
				})
		},
		clearRouteAttachmentDescription() {
			if (!this.currentRoute.routeAttachment || this.isRouteAttachmentUploading) {
				return
			}
			const ra = this.currentRoute.routeAttachment
			const savedNonEmpty = ra.description != null && String(ra.description).trim() !== ''
			const draftNonEmpty = String(this.routeAttachmentDescriptionDraft || '').trim() !== ''
			if (!savedNonEmpty && !draftNonEmpty) {
				return
			}
			this.routeAttachmentDescriptionDraft = ''
			if (!savedNonEmpty) {
				return
			}
			this.isClearingRouteAttachmentDescription = true
			this.$set(this.currentRoute.routeAttachment, 'description', '')
			this.$store
				.dispatch('routes/updateRoute')
				.then(() => {
					this.syncRouteAttachmentDescriptionDraft()
					this.syncRouteAttachmentDescriptionPanelOpen()
					this.isRouteAttachmentDescriptionOpen = false
					this.$snackbar.open('Opis załącznika został usunięty.')
					bus.$emit('route-updated')
				})
				.catch((errors) => {
					this.$toast.open({
						type: 'is-danger',
						duration: 6000,
						message: extractUploadErrorMessage(errors)
					})
				})
				.finally(() => {
					this.isClearingRouteAttachmentDescription = false
				})
		},
		save() {
			this.isLoading = true
			this.$store.dispatch('routes/updateRoute').then((route) => {
				this.isLoading = false
				this.isEditModeEnabled = false
				this.$snackbar.open(`Trasa #${route.id} została edytowana.`)
				bus.$emit('route-updated')
			})
		},
		archive() {
			this.isLoading = true
			this.$store.dispatch('routes/archiveRoute').then((route) => {
				this.isLoading = false
				this.$parent.$emit('close')
				this.$snackbar.open(`Trasa #${route.id} została zarchiwizowana.`)
				bus.$emit('route-archived')
			})
		},
		getStatusCssClass(status) {
			switch (status) {
				case 'new':
					return 'is-success'
				case 'planned':
					return 'is-warning'
				case 'sent':
					return 'is-info'
				case 'archived':
					return 'is-danger'
			}
		},
		changeStatus(status) {
			this.isLoading = true
			this.$store.dispatch('routes/updateRouteStatus', status).then((route) => {
				this.isLoading = false
				this.$snackbar.open(
					`Status trasy #${route.id} został zmieniony na "${this.$options.filters.formatStatus(
						status
					)}".`
				)
				bus.$emit('route-updated')
			})
		},
		showDeleteDialog() {
			this.$dialog.confirm({
				title: `Usuwanie trasy: #${this.currentRoute.id}`,
				message: `Czy na pewno usunąć trasę "${this.currentRoute.name}"?`,
				confirmText: 'Tak, usuń',
				cancelText: 'Anuluj',
				type: 'is-danger',
				hasIcon: true,
				onConfirm: () => this.destroy()
			})
		},
		destroy() {
			this.isLoading = true
			this.$store.dispatch('routes/deleteRoute', this.currentRoute).then((route) => {
				this.isLoading = false
				this.$snackbar.open(`Trasa #${route.id} zostało usunięta.`)
				this.$parent.$emit('close')
				bus.$emit('route-deleted')
			})
		},
		changeMontageTime(order, dates) {
			if (!Array.isArray(dates) || !dates.length || !(dates[0] instanceof Date)) {
				this.isChangingTimeModeActive = false
				return
			}
			const pickedDate = dates[0]
			const hour = String(pickedDate.getHours()).padStart(2, '0')
			const minute = String(pickedDate.getMinutes()).padStart(2, '0')
			const montageTime = `${hour}:${minute}`
			if (normalizeApiMontageTime(order.montageTime) === montageTime) {
				this.isChangingTimeModeActive = false
				return
			}
			const orderToUpdate = { ...order, montageTime }
			this.isLoading = true
			this.isOrderUpdating = true
			this.$store.dispatch('orders/updateOrder', orderToUpdate).then((order) => {
				this.$store.dispatch('routes/setCurrentRoute', this.currentRoute).then(() => {
					this.isLoading = false
					this.isOrderUpdating = false
					this.isChangingTimeModeActive = false
					bus.$emit('order-updated')
					this.$snackbar.open(`Godzina montażu dla zamówienia #${order.id} została zmieniona.`)
				})
			})
		},
		changeMontageDate(order, dates) {
			if (!Array.isArray(dates) || !dates.length || !(dates[0] instanceof Date)) return
			const pickedDate = dates[0]
			const day = String(pickedDate.getDate()).padStart(2, '0')
			const month = String(pickedDate.getMonth() + 1).padStart(2, '0')
			const year = pickedDate.getFullYear()
			const montageDate = `${day}-${month}-${year}`
			if (normalizeApiMontageDate(order.montageDate) === montageDate) return
			const orderToUpdate = { ...order, montageDate }
			this.isLoading = true
			this.isOrderUpdating = true
			this.$store.dispatch('orders/updateOrder', orderToUpdate).then((order) => {
				this.$store.dispatch('routes/setCurrentRoute', this.currentRoute).then(() => {
					this.isLoading = false
					this.isOrderUpdating = false
					bus.$emit('order-updated')
					this.$snackbar.open(`Data montażu dla zamówienia #${order.id} została zmieniona.`)
				})
			})
		},
		setChangingTimeModeActive() {
			this.isChangingTimeModeActive = true
		},
		normalizeMontageTime(montageTime) {
			return normalizeApiMontageTime(montageTime)
		},
		normalizeMontageDate(montageDate) {
			return normalizeApiMontageDate(montageDate)
		},
		clearMontageDateTime(order) {
			const orderToUpdate = { ...order, montageTime: null, montageDate: null }
			this.isLoading = true
			this.isOrderUpdating = true
			this.$store.dispatch('orders/updateOrder', orderToUpdate).then((order) => {
				this.isLoading = false
				this.$snackbar.open(
					`Data i godzina montażu dla zamówienia #${order.id} zostały zresetowane.`
				)
				bus.$emit('order-updated')
			})
		},
		changeIsApprovedStatus(order, isApproved) {
			const orderToUpdate = { id: order.id, isApproved }
			this.isLoading = true
			this.isOrderUpdating = true
			this.$store.dispatch('orders/updateOrder', orderToUpdate).then((order) => {
				this.$store.dispatch('routes/setCurrentRoute', this.currentRoute).then(() => {
					this.isLoading = false
					this.isOrderUpdating = false
					bus.$emit('order-updated')
					this.$snackbar.open(`Zamówienie #${order.id} zostało zatwierdzone.`)
				})
			})
		},
		changeIsInvoiceSentStatus(order, isInvoiceSent) {
			const orderToUpdate = { id: order.id, isInvoiceSent }
			this.isLoading = true
			this.isOrderUpdating = true
			this.$store.dispatch('orders/updateOrder', orderToUpdate).then((order) => {
				this.$store.dispatch('routes/setCurrentRoute', this.currentRoute).then(() => {
					this.isLoading = false
					this.isOrderUpdating = false
					bus.$emit('order-updated')
					this.$snackbar.open(`Zmieniono status dla zamówienia #${order.id} - faktura wysłana.`)
				})
			})
		},
		changeIsInvoicePaidStatus(order, isInvoicePaid) {
			const orderToUpdate = { id: order.id, isInvoicePaid }
			this.isLoading = true
			this.$store.dispatch('orders/updateOrder', orderToUpdate).then((order) => {
				this.$store.dispatch('routes/setCurrentRoute', this.currentRoute).then(() => {
					this.isLoading = false
					this.isOrderUpdating = false
					bus.$emit('order-updated')
					this.$snackbar.open(`Zmieniono status dla zamówienia #${order.id} - faktura zapłacona.`)
				})
			})
		},
		sendEmail(orderId, method) {
			this.isLoading = true
			this.$store
				.dispatch('orders/sendEmail', { orderId, userId: this.user.id, method })
				.then(({ data }) => {
					this.$snackbar.open(`Email został wysłany do ${data.recipient}`)
					this.$store.dispatch('routes/setCurrentRoute', this.currentRoute).then(() => {
						this.isLoading = false
						bus.$emit('order-updated')
					})
				})
				.catch(() => {
					this.isLoading = false
					this.$snackbar.open(`Wystąpił błąd podczas wysyłania wiadomości email.`)
				})
		},
		getRouteDistances(orders) {
			const directionsClient = mbxDirections({ accessToken: config.mapboxAccessToken })

			const waypoints = []

			if (this.currentRoute.startPoint) {
				const lat = Number(JSON.parse(this.currentRoute.startPoint).lat)
				const lon = Number(JSON.parse(this.currentRoute.startPoint).lon)
				waypoints.push({ coordinates: [lon, lat] })
			}

			const ordersToUse = orders || this.routeOrders

			ordersToUse
				.filter(({ lat, lon }) => lat && lon)
				.forEach(({ id, lat, lon }) => {
					waypoints.push({ waypointName: 'order:' + id, coordinates: [Number(lon), Number(lat)] })
				})

			if (waypoints.length < 2)
				return Promise.reject('Nie można wyliczyć odległości - potrzeba co najmniej 2 lokalizacji.')

			if (waypoints.length > 25) {
				const promises = []
				const chunkSize = 24
				for (let i = 0, j = waypoints.length; i < j; i += chunkSize) {
					const chunk = waypoints.slice(i === 0 ? i : i - 1, i + chunkSize)
					const promise = directionsClient
						.getDirections({
							profile: 'driving',
							waypoints: chunk,
							geometries: 'geojson'
						})
						.send()
					promises.push(promise)
				}
				return Promise.all(promises).then((response) => {
					const result = {
						uuid: '',
						routes: [
							{
								distance: 0,
								duration: 0,
								geometry: {
									coordinates: [],
									type: 'LineString'
								},
								legs: []
							}
						]
					}
					response.forEach(({ body }) => {
						result.uuid = body.uuid
						if (result.routes[0]) {
							result.routes[0].distance += body.routes[0].distance
							result.routes[0].duration += body.routes[0].duration
							result.routes[0].geometry.coordinates = [
								...result.routes[0].geometry.coordinates,
								...body.routes[0].geometry.coordinates
							]
							result.routes[0].legs = [...result.routes[0].legs, ...body.routes[0].legs]
						}
					})
					return { body: result }
				})
			}

			return directionsClient
				.getDirections({
					// profile: 'driving-traffic', // up to 3 max (includes traffic)
					profile: 'driving', // up to 25 max
					waypoints,
					geometries: 'geojson'
				})
				.send()
		},
		updateDistances(routeSegments) {
			const distances = []

			routeSegments.forEach(({ distance }) => {
				distances.push(Math.round(distance / 1000))
			})
			this.distances = distances

			return distances
		},
		addRouteLayer(coordinates, routeGeometryUuid) {
			// remove previous layer
			if (this.routeGeometryUuid) {
				const mapLayer = this.map.getLayer(this.routeGeometryUuid)
				if (mapLayer)
					this.map.removeLayer(this.routeGeometryUuid).removeSource(this.routeGeometryUuid)
			}
			this.routeGeometryUuid = routeGeometryUuid

			this.map.addLayer({
				id: this.routeGeometryUuid,
				type: 'line',
				source: {
					type: 'geojson',
					data: {
						type: 'Feature',
						properties: {},
						geometry: {
							type: 'LineString',
							coordinates
						}
					}
				},
				layout: {
					'line-join': 'round',
					'line-cap': 'round'
				},
				paint: {
					'line-color': '#209cee',
					'line-width': 6
				}
			})
		},
		initMap() {
			if (!this.agentsCanEditEverything) return
			mapboxgl.accessToken = config.mapboxAccessToken
			this.map = new mapboxgl.Map({
				// preserveDrawingBuffer: true,
				container: 'route-map',
				style: 'mapbox://styles/mapbox/streets-v9'
			})
		},
		centerMap(isInstant = false) {
			if (!this.map) {
				console.log(
					'Map is not initialized yet. This can happen when user is not allowed to see the map.'
				)
				return
			}

			const that = this
			this.isMapCentering = true
			return new Promise(function (resolve) {
				if (!that.routeCoordinates.length) return
				// calculate orders bound and center map to them
				const bounds = that.routeCoordinates.reduce(function (bounds, coord) {
					return bounds.extend(coord)
				}, new mapboxgl.LngLatBounds(that.routeCoordinates[0], that.routeCoordinates[0]))

				that.map.fitBounds(
					bounds,
					{
						padding: 40,
						maxZoom: 8,
						duration: isInstant ? 0 : 500
					},
					{ isFitting: true }
				)

				setTimeout(
					() => {
						that.takeScreenshot(that.map).then((img) => {
							that.mapImage = img
							that.isMapCentering = false
							that.$nextTick(() => {
								resolve()
							})
						})
					},
					isInstant ? 200 : 1000
				)
			})
		},
		getMarkerIconName(index, length, isStartPointSet, onMap = false) {
			// start marker
			if (index === 0 && (!isStartPointSet || onMap)) return 'pin_drop'

			// finish marker
			if (((onMap || isStartPointSet) && index === length - 1) || (!onMap && index === length))
				return 'flag'

			// middle marker
			return 'place'
		},
		clearMarkers() {
			this.markers.forEach((marker) => marker.remove())
			this.markers = []
		},
		addOrderMarkers(orders) {
			if (!this.routeOrders && !orders) return

			if (this.markers.length) this.clearMarkers()

			let markerOrders = []

			if (this.currentRoute.startPoint) {
				const lat = Number(JSON.parse(this.currentRoute.startPoint).lat)
				const lon = Number(JSON.parse(this.currentRoute.startPoint).lon)
				markerOrders.push({
					id: 'Start',
					lat,
					lon
				})
			}

			const ordersToUse = orders || this.routeOrders

			const ordersWithCoordinates = ordersToUse.filter(({ lat, lon }) => lat && lon)
			markerOrders = [...markerOrders, ...ordersWithCoordinates]

			markerOrders.forEach((order, index) => {
				const customMarker = document.createElement('div')
				customMarker.className = 'custom-marker'

				const markerText = document.createElement('span')
				markerText.innerHTML = '# ' + order.id

				const markerIcon = document.createElement('i')
				markerIcon.className = 'mdi'
				markerIcon.innerHTML = this.getMarkerIconName(
					index,
					markerOrders.length,
					!!this.currentRoute.startPoint,
					true
				)

				customMarker.appendChild(markerText)
				customMarker.appendChild(markerIcon)

				const marker = new mapboxgl.Marker({
					element: customMarker
				})
					.setLngLat([order.lon, order.lat])
					.addTo(this.map)

				this.markers.push(marker)
			})
		},
		onMoveDraggable(event) {
			if (event.related.hasAttribute('disabled') || event.dragged.hasAttribute('disabled'))
				return false
		},
		getCurrencyLabel(currency) {
			switch (currency) {
				case 'PLN':
					return 'zł'
				case 'CZK':
					return 'Kč'
				default:
					return currency
			}
		},
		getOrderCreatorId(order) {
			// const orderCreatorSplited = order.creator.split('/')
			// return Number(orderCreatorSplited[orderCreatorSplited.length - 1])

			if (!order.creator) return ''
			if (order.creator.id) return order.creator.id
			if (typeof order.creator !== 'string') return ''
			const orderCreatorSplited = order.creator.split('/')
			return Number(orderCreatorSplited[orderCreatorSplited.length - 1])
		},
		getRouteCreatorId(route) {
			if (!route.creator) return ''
			if (route.creator.id) return route.creator.id
			if (typeof route.creator !== 'string') return ''
			const routeCreatorSplited = route.creator.split('/')
			return Number(routeCreatorSplited[routeCreatorSplited.length - 1])
		},
		findLocation() {
			this.isLocationFormVisible = true
			this.areLocationsLoading = true
			this.$store.dispatch('orders/findLocation').then((locations) => {
				if (!locations.length) {
					this.$toast.open({
						type: 'is-danger',
						message: 'Nie znaleziono lokalizacji dla podanych danych adresowych.'
					})
					this.isLocationFormVisible = false
				} else this.locations = locations
				this.areLocationsLoading = false
			})
		},
		getStartPointLocation: debounce(function (name) {
			if (!name.length) {
				this.filteredDataArray = []
				return
			}
			this.isStartPointFetching = true

			LocationService.geocode(name)
				.then((locations) => {
					console.log({ locations })
					if (!locations.length) {
						this.$toast.open({
							type: 'is-danger',
							message: 'Nie znaleziono lokalizacji dla podanego adresu.'
						})
					} else this.filteredDataArray = locations
				})
				.catch((error) => {
					this.filteredDataArray = []
					throw error
				})
				.finally(() => {
					this.isStartPointFetching = false
				})
		}, 500),
		saveStartPoint() {
			console.log('saveStartPoint', this.startPoint)
			this.isLoading = true
			this.$store
				.dispatch('routes/updateRouteStartPoint', this.startPoint)
				.then(() => {
					this.addOrderMarkers()

					this.getRouteDistances()
						.then(({ body }) => {
							if (!body.routes.length) {
								this.isLoading = false
								const error =
									'Nie można wyznaczyć trasy, proszę spróbować zmienić kolejność zamówień lub punkty na trasie.'
								this.$snackbar.open(error)
								console.log('getRouteDistances error', error)
								return
							}

							this.updateDistances(body.routes[0].legs)
							this.addRouteLayer(body.routes[0].geometry.coordinates, body.uuid)
							this.centerMap()

							const sortedOrdersIds = this.currentRoute.ordersIds

							this.$store
								.dispatch('routes/updateRouteOrdersOrderAndDistances', {
									sortedOrdersIds,
									distances: this.distances
								})
								.then((route) => {
									this.isLoading = false
									this.$snackbar.open(
										`Kolejność zamówień dla trasy #${route.id} została zmieniona.`
									)
									bus.$emit('route-updated')
								})
						})
						.catch((errorMsg) => {
							console.log('error', errorMsg)
							this.$snackbar.open(errorMsg)
						})

					this.$snackbar.open(`Punkt startowy został zmieniony.`)
					bus.$emit('route-updated')
					this.isChangeStartPointModeEnabled = false
				})
				.catch((error) => {
					this.$snackbar.open(`Wystąpił błąd i punkt startowy nie został zmieniony.`)
					console.log('saveStartPoint error', { error })
				})
				.finally(() => {
					this.isLoading = false
				})
		},
		deleteStartPoint() {
			console.log('deleteStartPoint', this.startPoint)
			this.isLoading = true
			this.$store
				.dispatch('routes/updateRouteStartPoint', null)
				.then(() => {
					this.addOrderMarkers()

					this.getRouteDistances()
						.then(({ body }) => {
							if (!body.routes.length) {
								this.isLoading = false

								const error =
									'Nie można wyznaczyć trasy, proszę spróbować zmienić kolejność zamówień lub punkty na trasie.'
								this.$snackbar.open(error)
								console.log('getRouteDistances error', error)
								return
							}

							this.updateDistances(body.routes[0].legs)
							this.addRouteLayer(body.routes[0].geometry.coordinates, body.uuid)
							this.centerMap()

							const sortedOrdersIds = this.currentRoute.ordersIds

							this.$store
								.dispatch('routes/updateRouteOrdersOrderAndDistances', {
									sortedOrdersIds,
									distances: this.distances
								})
								.then((route) => {
									this.isLoading = false
									this.$snackbar.open(
										`Kolejność zamówień dla trasy #${route.id} została zmieniona.`
									)
									bus.$emit('route-updated')
								})
						})
						.catch((errorMsg) => {
							console.log('error', errorMsg)
							this.$snackbar.open(errorMsg)
						})

					this.$snackbar.open(`Punkt startowy został usunięty.`)
					bus.$emit('route-updated')
				})
				.catch((error) => {
					this.$snackbar.open(`Wystąpił błąd i punkt startowy nie został usunięty.`)
					console.log('saveStartPoint error', { error })
				})
				.finally(() => {
					this.isLoading = false
				})
		},
		reverseOrder() {
			console.log(
				'reverseOrder',
				this.routeOrders.map(({ id }) => id)
			)
			this.routeOrders = this.routeOrders.reverse()
			console.log(
				'after reverse',
				this.routeOrders.map(({ id }) => id)
			)
		},
		takeScreenshot(map) {
			return new Promise(function (resolve) {
				map.once('render', function () {
					const mapEl = document.querySelector('#route-map')
					if (!mapEl) return
					html2canvas(mapEl).then(function (canvas) {
						resolve(canvas.toDataURL())
					})
				})
				/* trigger render */
				map.setBearing(map.getBearing())
			})
		},
		submitMapPdfForm() {
			// if map is not initialized yet then submit form immediately
			if (!this.map) {
				this.$refs.routeMapPdf.submit()
				return
			}

			const that = this
			const onResize = () => {
				window.removeEventListener('resize', onResize)
				that.centerMap(true).then(() => {
					that.takeScreenshot(that.map).then((img) => {
						that.mapImage = img
						that.$nextTick(() => {
							that.$refs.routeMapPdf.submit()
							that.isSubmitting = false
							that.$nextTick(() => {
								window.dispatchEvent(new Event('resize'))
								that.centerMap(true)
							})
						})
					})
				})
			}

			this.isSubmitting = true
			this.$nextTick(function () {
				window.addEventListener('resize', onResize)
				window.dispatchEvent(new Event('resize'))
			})
		},
		submitMapDocForm() {
			// if map is not initialized yet then submit form immediately
			if (!this.map) {
				this.$refs.routeMapDoc.submit()
				return
			}

			const onResize = () => {
				window.removeEventListener('resize', onResize)
				this.centerMap(true).then(() => {
					this.takeScreenshot(this.map).then((img) => {
						this.mapImage = img
						this.$nextTick(() => {
							this.$refs.routeMapDoc.submit()
							this.isSubmitting = false
							this.$nextTick(() => {
								window.dispatchEvent(new Event('resize'))
								this.centerMap(true)
							})
						})
					})
				})
			}

			this.isSubmitting = true
			this.$nextTick(function () {
				window.addEventListener('resize', onResize)
				window.dispatchEvent(new Event('resize'))
			})
		}
	},
	created() {
		this.prevRouteOrders = [...this.routeOrders]
	},
	mounted() {
		if (this.canModifyRoute) {
			this.initMap()

			this.map.on('load', () => {
				this.addOrderMarkers()

				if (this.routeOrders.length > 1) {
					const sortedOrdersIds = this.currentRoute.ordersIds
					this.getRouteDistances()
						.then(({ body }) => {
							if (!body.routes.length) {
								const error =
									'Nie można wyznaczyć trasy, proszę spróbować zmienić kolejność zamówień lub punkty na trasie.'
								this.$snackbar.open(error)
								console.log('getRouteDistances error', error)
								return
							}

							this.updateDistances(body.routes[0].legs)
							this.addRouteLayer(body.routes[0].geometry.coordinates, body.uuid)
							if (this.routeOrders.length) this.centerMap()

							this.$store
								.dispatch('routes/updateRouteOrdersOrderAndDistances', {
									sortedOrdersIds,
									distances: this.distances
								})
								.then((route) => {
									this.isLoading = false
									this.$snackbar.open(
										`Informacja o odległościach dla trasy #${route.id} została zaktualizowana.`
									)
									bus.$emit('route-updated')
								})
						})
						.catch((error) => {
							this.$snackbar.open(error)
							console.log('getRouteDistances error', error)
						})
				}
			})
		}
	},
	destroyed() {
		this.$store.dispatch('routes/setCurrentRoute', null)
	}
}
</script>

<style lang="scss" scoped>
.field-body .field .field {
	margin-bottom: 10px;
}
.action-buttons {
	justify-content: space-between !important;
}
.modal-card-body {
	padding-bottom: 50px;
}
.change-color {
	margin-left: 10px;
}
.route-color {
	&__preview {
		width: 30px;
		height: 30px;
		display: inline-block;
		vertical-align: middle;
		margin-right: 10px;
		border-radius: 3px;
		border: 1px solid #ccc;
	}
}
.no-route-color {
	color: #ccc;
}
.orders {
	padding-top: 25px;
	ul {
		/* list-style: circle; */
		/* margin-left: 10px; */
		list-style: none;
		margin-left: 0;
	}
}
.change-start-point-wrapper {
	padding: 0 25px 0;
}
.start-point-actions {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 10px;
}
.start-point-help {
	margin-top: 6px;
}
.start-point-address {
	margin-bottom: 0.75rem;
}
.cancel-start-point {
	margin-right: 0.75rem;
}
.no-orders-info {
	padding: 0 25px 25px;
}
.orders-list {
	padding: 0 25px;
}
.created-date {
	display: block;
	color: #aaa;
}
.is-pdf-link,
.is-doc-link {
	margin-right: 10px;
	margin-top: 5px;
}
.order-item {
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 20px;
	padding: 15px 0 15px 0;
	background-color: #edf9f7;
	border-radius: 5px;
	transition: background-color 0.3s, border-color 0.3s;
	border: 2px dashed #d9f3ee;
	cursor: move;
	.order-no,
	.montage,
	.action {
		padding: 0 20px;
		flex-grow: 0;
	}
	&:hover {
		background-color: #d3f3ed;
		border-color: #d3f3ed;
		border: 2px dashed #d1efea;
	}
	&[disabled]:not(.order-item--start) {
		cursor: not-allowed;
		background-color: #fff5f5;
		border: 2px dashed #f7d9d9;
	}
	&.order-item--start {
		cursor: auto;
		margin: 0 25px 25px;
		background-color: #edf4f9;
		border: 2px dashed #d9ebf3;
	}
	& > * {
		border-right: 1px dashed #ddd;
		text-align: center;
		line-height: 24px;
		flex-grow: 1;

		&:last-child {
			border-right: none;
		}
	}
	.no-localization {
		color: red;
	}
	.montage-date {
		margin-bottom: 10px;
	}
	.time-input,
	.date-input {
		background: none;
		border: none;
		font-size: 14px;
		color: #3273dc;
		border-bottom: 1px dashed #3273dc;
	}
	.time-input {
		width: 40px;
		margin: 0 10px;
	}
	.date-input {
		width: 75px;
	}
	.phone,
	.email {
		font-size: 13px;
		.mdi {
			font-size: 14px;
			padding-right: 2px;
		}
	}
	.flag {
		position: absolute;
		width: 20px;
		top: 0;
		right: 0;
	}
	.action {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		.email-sent-label,
		.email-sent-date {
			line-height: 18px;
		}
		.email-sent-date {
			margin-bottom: 10px;
			font-size: 13px;
		}
	}
}
.flip-list-move {
	transition: transform 0.5s;
}
.no-move {
	transition: transform 0s;
}
.ghost {
	opacity: 0.5;
}
#route-map {
	width: 100%;
	height: 300px;

	&.pdf {
		width: 1000px;
		height: 600px;
		position: fixed;
		z-index: 100;
		top: -10000px;
		left: 380px;
		z-index: -100;
	}
}
.custom-marker {
	font-size: 30px;
}
.map-controls {
	margin: 10px;
}

.total-prices {
	flex-direction: column;
	.flag {
		width: 20px;
		box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
	}
}
.reverse-order-btn {
	margin-left: 0;
}
.route-route-attachment {
	margin: 20px 0;
	min-width: 0;
	.route-route-attachment__empty {
		padding: 0.25rem 0;
	}
	.route-route-attachment__body {
		min-width: 0;
	}
	.route-attachment-desc-toggle {
		font-weight: 500;
		border-color: #dbdee4;
		color: #4b5563;
	}
	.route-attachment-desc-toggle:hover {
		color: #1f2937;
		border-color: #c9ced6;
	}
	.picture__empty {
		color: #ccc;
		margin: 0;
	}
	.route-attachment__deleting {
		position: relative;
		min-height: 120px;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.attachments-list {
		display: flex;
		flex-direction: column;
		gap: 0.85rem;
	}
	.attachment-item {
		padding: 0.75rem 0.85rem;
		border: 1px solid #e8ebf0;
		border-radius: 8px;
		background: #fafbfd;
		min-width: 0;
	}
	.attachment-item__layout {
		display: flex;
		gap: 0.85rem;
		align-items: flex-start;
		min-width: 0;
	}
	.attachment-item__preview {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 3.25rem;
		width: 3.25rem;
		align-self: flex-start;
		padding-top: 0.15rem;
	}
	.attachment-item__preview-loading {
		position: relative;
		width: 100%;
		min-height: 3.25rem;
		display: block;
		overflow: visible;
	}
	.attachment-item__main {
		flex: 1 1 0;
		min-width: 0;
		display: flex;
		flex-direction: column;
	}
	.attachment-item__header-row {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
		justify-content: space-between;
		gap: 0.65rem 1rem;
	}
	.attachment-item__title-block {
		flex: 1 1 22rem;
		min-width: 0;
	}
	.route-attachment-filename {
		display: inline-flex;
		align-items: center;
		gap: 0.35rem;
		max-width: 100%;
		font-size: 0.9rem;
		font-weight: 600;
		color: #1e40af;
		line-height: 1.35;
		text-decoration: none;
		margin: 0 0 0.25rem 0;
	}
	.route-attachment-filename__text {
		min-width: 0;
		overflow: hidden;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		white-space: normal;
		word-break: break-word;
		overflow-wrap: anywhere;
		flex: 1 1 auto;
	}
	.route-attachment-filename__ext-icon {
		font-size: 1rem;
		opacity: 0.75;
		flex-shrink: 0;
	}
	.route-attachment-filename:hover {
		color: #1d4ed8;
		text-decoration: underline;
	}
	.attachment-item__meta {
		font-size: 0.78rem;
		color: #64748b;
		margin: 0;
		line-height: 1.4;
	}
	.attachment-item__header-actions {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 0.45rem;
		flex-shrink: 0;
	}
	.attachment-item__description-panel {
		margin-top: 0.75rem;
		padding-top: 0.75rem;
		border-top: 1px solid #e8ebf0;
	}
	.attachment-item__save-row {
		display: flex;
		justify-content: flex-end;
		flex-wrap: wrap;
		gap: 0.5rem;
		margin-top: 0.5rem;
	}
	.attachment-item__desc-read {
		font-size: 0.82rem;
		color: #666;
		margin: 0.35rem 0 0 0;
	}
	.attachment-item__desc {
		margin-bottom: 0 !important;
		width: 100%;
	}
	.attachment-item__desc .control,
	.attachment-item__desc textarea {
		width: 100%;
		max-width: 100%;
	}
	.attachment-item__desc textarea {
		min-height: 6.5rem;
	}
	.route-attachment-change-upload {
		display: inline-block;
		vertical-align: middle;
	}
	.route-attachment-change-upload.upload {
		display: inline-block;
	}
	.attachment-upload-tile {
		width: 100%;
	}
	.attachment-upload-tile__control {
		display: block;
		width: 100%;
	}
	.attachment-upload-tile__control .upload {
		display: block;
		width: 100%;
	}
	.attachment-upload-tile__inner {
		position: relative;
		width: 100%;
		min-height: 88px;
		padding: 0.5rem 0.75rem;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		align-items: center;
		justify-content: center;
		gap: 0.45rem;
		border: 1px dashed #d8dee8;
		border-radius: 8px;
		background: #fbfcfe;
		color: #111827;
		font-weight: 500;
		cursor: pointer;
		font-size: 1rem;
	}
	.attachment-upload-tile__inner small {
		font-size: 0.85rem;
		color: #98a2b3;
	}
	.attachment-upload-tile__text {
		font-size: 0.95rem;
	}
	.attachment-upload-tile__hint-line {
		flex-basis: 100%;
		text-align: center;
		font-size: 0.8rem;
		font-weight: 400;
		color: #6b7280;
		line-height: 1.35;
		margin: 0;
	}
	.attachment-upload-tile__spinner {
		position: relative;
		width: 100%;
		min-height: 72px;
		overflow: visible;
	}
	.route-doc-badge-link {
		display: block;
		width: 100%;
		text-decoration: none;
		color: inherit;
	}
	.route-doc-badge--compact {
		display: flex;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;
		width: 3.25rem;
		height: 3.25rem;
		min-height: 0;
		padding: 0;
		border-radius: 8px;
		border: 1px solid #e8ebf0;
		background: linear-gradient(180deg, #fafbfd 0%, #eef2f7 100%);
		transition: box-shadow 0.15s ease, transform 0.15s ease;
	}
	.route-doc-badge--compact:hover {
		box-shadow: 0 3px 10px rgba(15, 23, 42, 0.1);
	}
	.route-doc-badge--compact.route-doc-badge--pdf,
	.route-doc-badge--compact.route-doc-badge--doc,
	.route-doc-badge--compact.route-doc-badge--docx,
	.route-doc-badge--compact.route-doc-badge--unknown {
		border-color: #93c5fd;
		background: linear-gradient(180deg, #f8fafc 0%, #bfdbfe 100%);
	}
	.route-doc-badge--compact .route-doc-badge__ext-chip {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.2rem 0.38rem 0.22rem;
		border-radius: 4px;
		background: #1e40af;
		color: #fff;
		font-size: 0.52rem;
		font-weight: 800;
		letter-spacing: 0.03em;
		line-height: 1;
		text-transform: uppercase;
		user-select: none;
	}
}
@media (max-width: 900px) {
	.route-route-attachment .attachment-item__layout {
		flex-direction: column;
	}
	.route-route-attachment .attachment-item__preview {
		align-self: flex-start;
	}
	.route-route-attachment .attachment-item__header-actions {
		width: 100%;
		justify-content: flex-start;
	}
}
.item-email {
	font-weight: 500;
}
</style>
